import { logger } from '@magicschool/logger';
import { getAdobeSdk } from './sdk';
import type { Callbacks, HostAppTrigger, ThumbnailOption } from './types';

const ADOBE_Z_INDEX = 1_000_000;

const containerConfig = {
  zIndex: ADOBE_Z_INDEX,
} as const;

const exportOptions = [
  {
    id: 'save-modified-asset',
    label: 'Save to MagicSchool',
    action: {
      target: 'publish',
    },
    style: {
      uiType: 'button',
    },
  } as const,
];

export type OpenAdobeGenerateImageModule = {
  onPublish: Callbacks['onPublish'];
  onCancel?: Callbacks['onCancel'];
  promptText?: string;
  isStudent?: boolean;
};

export async function forceCloseAdobeGenerateImageModule() {
  try {
    const adobe = await getAdobeSdk();
    if (adobe?.close) {
      adobe.close();
    } else {
      logger.warn('Adobe module close function not available after SDK load');
    }
  } catch (e) {
    logger.error('Error closing Adobe module or loading SDK:', e);
  }
}

export async function openAdobeGenerateImageModule({ onPublish, onCancel, promptText, isStudent }: OpenAdobeGenerateImageModule) {
  const thumbnailOptions: ThumbnailOption[] = isStudent ? ['rich-preview', 'publish'] : ['rich-preview', 'download', 'publish'];

  try {
    const adobe = await getAdobeSdk();
    adobe.module.createImageFromText(
      {
        promptText,
        appVersion: '2',
        featureConfig: {
          'community-wall': false,
        },
        thumbnailOptions: thumbnailOptions,
        publishConfig: {
          id: 'saveToHostApp',
          label: 'Save to MagicSchool',
        },
        useClientAuth: true,
        callbacks: {
          onPublish: onPublish,
          onCancel: onCancel,
          onIntentChange: () => {
            return {
              appConfig: {
                callbacks: {
                  onPublish: onPublish,
                },
                allowedFileTypes: ['image/jpeg', 'image/png'],
              },
              containerConfig,
              exportConfig: [
                {
                  type: 'button',
                  id: 'saveToHostApp',
                  label: 'Save to MagicSchool',
                  action: {
                    target: 'publish',
                  },
                  style: {
                    uiType: 'button',
                  },
                },
              ],
            };
          },
        },
      },
      [
        {
          id: 'save-modified-asset',
          label: 'Save to MagicSchool',
          action: {
            target: 'publish',
          },
          style: {
            uiType: 'button',
          },
        },
      ],
      { zIndex: ADOBE_Z_INDEX },
    );
  } catch (error) {
    logger.error('Failed to open Adobe Generate Image module or load SDK:', error);
    // Optionally, notify the user or handle the error in a way that makes sense for the application
  }
}

export async function openAdobeEditImageModule(
  data: string,
  onPublish: Callbacks['onPublish'],
  dataType: 'url' | 'base64',
  hostAppTrigger: HostAppTrigger,
) {
  await getAdobeSdk();
  window.adobe?.module.editImage(
    {
      asset: {
        type: 'image',
        dataType,
        data,
      },
    },
    {
      callbacks: {
        onPublish: onPublish,
      },
      allowedFileTypes: ['image/jpeg', 'image/png'],
      appVersion: '2',
      analyticsData: {
        hostAppTrigger: hostAppTrigger,
      },
    },
    exportOptions,
    containerConfig,
  );
}
